import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Tooltip } from '../tooltip';
import { Typography } from '../typography';

import styles from './desktop-navbar.scss';

export type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    title: string;
    subtitle?: string;
    left?: ReactNode;
    right?: ReactNode;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-desktop-navbar';

export const DesktopNavbar: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  ({ title, subtitle, left, right, dataTestId = COMPONENT_ID, className, ...rest }, ref) => {
    const titleRef = useRef<HTMLDivElement>(null);
    const [titleIsOverflowing, setTitleIsOverflowing] = useState<boolean>(false);

    const titleTypography = (
      <Typography
        ref={titleRef}
        variant={subtitle ? 'title-2-semibold' : 'title-1-semibold'}
        className={styles.title}>
        {title}
      </Typography>
    );

    useLayoutEffect(() => {
      setTitleIsOverflowing(titleRef.current?.scrollHeight > titleRef.current?.clientHeight);
    }, [title]);

    return (
      <div
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classNames(styles.wrapper, className)}
        ref={ref}
        {...rest}>
        {left && (
          <div
            className={classNames(styles.items, styles.left)}
            data-testid={`${dataTestId}-left-section`}
            data-component-name={`${dataTestId}-left-section`}>
            {left}
          </div>
        )}

        <div
          className={styles.center}
          data-testid={`${dataTestId}-center-section`}
          data-component-name={`${dataTestId}-center-section`}>
          {titleIsOverflowing ? (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <Tooltip text={title} placement="top" maxTextLength={9999}>
              {titleTypography}
            </Tooltip>
          ) : (
            titleTypography
          )}

          {subtitle && (
            <Typography variant="subhead-regular" className={styles.subtitle}>
              {subtitle}
            </Typography>
          )}
        </div>

        {right && (
          <div
            className={classNames(styles.items, styles.right)}
            data-testid={`${dataTestId}-right-section`}
            data-component-name={`${dataTestId}-right-section`}>
            {right}
          </div>
        )}
      </div>
    );
  },
);

DesktopNavbar.displayName = 'DesktopNavbar';
